import { Descriptions, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type BmwModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/BmwModuleWithPermissionsSpecs';
import { type BmwModuleUpdateSettings } from '../../../../api/types';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import BmwModuleAction from './BmwModuleAction';
import BmwModuleInnerPage from './BmwModuleInnerPage';

export type BmwModulePageProps = { module: BmwModuleWithPermissionsSpecsFragment };
export type BmwFormValues = BmwModuleUpdateSettings;

export enum HeaderTabs {
    Configuration = 'configuration',
    Authorization = 'authorization',
}

const BmwModulePage = ({ module }: BmwModulePageProps) => {
    const { t } = useTranslation(['bmwModuleDetails']);
    const [activeTab, setActiveTab] = useState(HeaderTabs.Configuration);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('bmwModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('bmwModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('bmwModuleDetails:content.financingModule')}>
                {module.bankModule.displayName}
            </Descriptions.Item>
            {module.optionalVehicleModule && (
                <Descriptions.Item label={t('bmwModuleDetails:content.vehicleModule')}>
                    {module.optionalVehicleModule.displayName}
                </Descriptions.Item>
            )}
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<BmwModuleAction module={module} permissions={permissions} />}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}>
                        <Tabs.TabPane
                            key={HeaderTabs.Configuration}
                            tab={t('bmwModuleDetails:tabs.configuration.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.Authorization}
                            tab={t('bmwModuleDetails:tabs.authorization.title')}
                        />
                    </Tabs>
                ),
            }}
        >
            <BmwModuleInnerPage activeTab={activeTab} module={module} />
        </ConsolePageWithHeader>
    );
};

export default BmwModulePage;
